(() => {
  if (customElements.get('bw-responsive-video')) return;

  function parseSources(raw) {
    try {
      const list = JSON.parse(raw || '[]');
      return Array.isArray(list) ? list.filter((s) => s && s.url && s.format === 'mp4') : [];
    } catch (_) {
      return [];
    }
  }

  function selectSource(sources, maxHeight) {
    const sorted = sources.slice().sort((a, b) => Number(a.height || 0) - Number(b.height || 0));
    return sorted.filter((s) => Number(s.height) > 0 && Number(s.height) <= maxHeight).pop() || sorted[0];
  }

  class ResponsiveVideo extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.video = this.querySelector('video');
      this.button = this.querySelector('.bw-responsive-video__toggle');
      this.status = this.querySelector('[data-video-status]');
      if (!this.video || !this.button || !this.status) return;
      this.connected = true;
      this.suspended = false;
      this.userPaused = this.userPaused || false;
      this.blocked = false;
      this.failed = false;
      this.pending = null;
      this.listeners = [];
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;

      // Select once, before setting any requestable src. Do not swap on resize/rotation.
      if (!this.selectedSource) {
        const desktop = parseSources(this.dataset.desktopSources);
        const mobile = parseSources(this.dataset.mobileSources);
        const small = window.matchMedia('(max-width: 768px)').matches;
        this.selectedSource = selectSource(small && mobile.length ? mobile : desktop, small ? 720 : 1080);
        this.dataset.sourceGroup = small ? 'mobile' : 'desktop';
      }
      if (!this.selectedSource) {
        this.failed = true;
        this.status.textContent = 'Video is unavailable.';
        this.dataset.state = 'error';
        return;
      }
      this.dataset.sourceHeight = String(this.selectedSource.height || '');
      this.inView = this.isInView();
      this.on(this.button, 'click', () => this.resume());
      // A blocked Safari autoplay can be resumed by tapping the video itself.
      // Keep the accessible button, but remove the visible play-icon overlay.
      this.on(this, 'click', event => {
        if (event.target === this.video || event.target === this) this.resume();
      });
      this.on(this.video, 'playing', () => {
        if (!this.canPlay()) this.pauseAutomatically();
        this.render();
      });
      this.on(this.video, 'pause', () => {
        // A native/user pause must not be undone by a later visibility event.
        if (!this.autoPausePending && !this.pending && this.canPlay() && !this.video.ended) this.userPaused = true;
        this.autoPausePending = false;
        this.render();
      });
      this.on(this.video, 'error', () => {
        this.failed = true;
        this.render();
      });
      this.on(this.video, 'ended', () => { this.userPaused = true; this.render(); });
      this.on(document, 'visibilitychange', () => this.reconcile());
      this.on(window, 'pagehide', () => { this.suspended = true; this.reconcile(); });
      this.on(window, 'pageshow', () => { this.suspended = false; this.inView = this.isInView(); this.reconcile(); });

      if ('IntersectionObserver' in window) {
        this.intersection = new IntersectionObserver((entries) => {
          const entry = entries[entries.length - 1];
          this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.1;
          this.reconcile();
        }, { threshold: [0, 0.1] });
        this.intersection.observe(this.video);
      } else {
        const update = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            this.inView = this.isInView();
            this.reconcile();
          });
        };
        this.on(window, 'scroll', update, { passive: true });
        this.on(window, 'resize', update, { passive: true });
      }

      // Observe only known theme overlays; do not modify their lifecycle or loader deadline.
      this.overlays = Array.from(document.querySelectorAll('[custom-loader], [data-loader-backdrop], #MlctrClose, #Details-menu-drawer-container, cart-drawer'));
      this.overlayObserver = new MutationObserver(() => this.reconcile());
      this.overlays.forEach((node) => {
        this.overlayObserver.observe(node, { attributes: true, attributeFilter: ['style', 'class', 'hidden', 'open'] });
        const removable = node.closest('#sectionpopup1') || node;
        if (removable.parentElement) this.overlayObserver.observe(removable.parentElement, { childList: true });
      });
      this.render();
      this.reconcile();
    }

    on(node, event, handler, options) {
      node.addEventListener(event, handler, options);
      this.listeners.push(() => node.removeEventListener(event, handler, options));
    }

    isInView() {
      const rect = this.video.getBoundingClientRect();
      const h = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      const w = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      return rect.width > 0 && rect.height > 0 && h * w / (rect.width * rect.height) >= 0.1;
    }

    covered() {
      return (this.overlays || []).some((node) => {
        if (!node.isConnected || node.hidden) return false;
        if (node.tagName === 'DETAILS' && !node.open) return false;
        if (node.tagName === 'CART-DRAWER' && !node.classList.contains('active')) return false;
        const style = getComputedStyle(node);
        return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && node.getClientRects().length > 0;
      });
    }

    canPlay() {
      return this.connected && !this.suspended && !document.hidden && this.inView && !this.userPaused && !this.blocked && !this.failed && !this.covered();
    }

    reconcile() {
      if (!this.connected) return;
      if (!this.canPlay()) this.pauseAutomatically();
      else if (this.video.paused && !this.pending) this.play();
      this.render();
    }

    pauseAutomatically() {
      if (this.pending) this.pending.cancelled = true;
      if (!this.video.paused) {
        this.autoPausePending = true;
        this.video.pause();
      }
    }

    play() {
      if (this.pending || !this.canPlay()) return;
      if (!this.sourceLoaded) {
        this.video.src = this.selectedSource.url;
        this.sourceLoaded = true;
        this.video.load();
      }
      const attempt = { cancelled: false };
      this.pending = attempt;
      let promise;
      try { promise = this.video.play(); } catch (error) { promise = Promise.reject(error); }
      Promise.resolve(promise).then(() => {
        if (this.pending !== attempt || !this.connected) return;
        if (!this.canPlay()) this.pauseAutomatically();
      }).catch((error) => {
        if (this.pending !== attempt || !this.connected) return;
        if (!(attempt.cancelled && error.name === 'AbortError')) {
          if (error.name === 'NotSupportedError') this.failed = true;
          else this.blocked = true;
        }
      }).finally(() => {
        if (this.pending !== attempt || !this.connected) return;
        this.pending = null;
        this.render();
        // A visibility change may have cancelled an earlier pending play request.
        if (attempt.cancelled && this.canPlay() && this.video.paused) this.play();
      });
    }

    resume() {
      // Recovery only: repeated activation must never pause a playing video.
      if (this.pending || (!this.video.paused && !this.failed && !this.blocked)) return;
      this.userPaused = false;
      this.blocked = false;
      if (this.failed) { this.failed = false; this.video.load(); }
      this.inView = this.isInView();
      // Keep play() in the click/keyboard activation task for Safari's gesture policy.
      this.play();
      this.render();
    }

    render() {
      const playing = !this.video.paused;
      const label = this.failed ? 'Retry video' : 'Play video';
      // No control during normal autoplay or automatic offscreen pauses.
      this.button.hidden = !!this.pending || !(this.failed || this.blocked || this.userPaused);
      this.button.textContent = label;
      this.button.setAttribute('aria-label', label);
      this.dataset.state = this.failed ? 'error' : this.blocked ? 'blocked' : playing ? 'playing' : 'paused';
      const message = this.failed ? 'Video could not load. Tap the video to retry.' : this.blocked ? 'Automatic playback is unavailable. Tap the video to play.' : '';
      if (this.status.textContent !== message) this.status.textContent = message;
    }

    disconnectedCallback() {
      this.connected = false;
      if (this.pending) this.pending.cancelled = true;
      this.pending = null;
      this.intersection?.disconnect();
      this.overlayObserver?.disconnect();
      (this.listeners || []).forEach((remove) => remove());
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.video?.pause();
    }
  }

  customElements.define('bw-responsive-video', ResponsiveVideo);
})();
